/**
 * useOrgAnonymizer — centralizes RBAC-aware org name masking + filter
 * logic used across benchmarking surfaces (Org Benchmarks, PE
 * Benchmarking, future industry/region benchmarking pages).
 *
 * Single source of truth for:
 *   - peer org relabeling ("Acme Industries" → "Org A")
 *   - scoped-tenant "(You)" decoration
 *   - Industry / Region / Company Size lookups + chips
 *   - page-level filter predicate (matchesFilters)
 *   - narrative + cohort-name regex redaction
 *   - OrgLabel React component + renderOrgLabel function shims
 *
 * Previously these were duplicated with subtle drift across pages — any
 * bug fix had to be ported to each copy. This hook removes that risk.
 */
import React, { useMemo } from 'react';

const SIZE_ORDER = ['startup', 'small', 'medium', 'large', 'enterprise'];
const _sizeLabel = (slug) =>
  slug ? slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null;

// Pure utility re-exported so Select options and other peripheral UIs
// can pretty-print a raw company-size slug without having to run the
// full hook (e.g. option labels in filter dropdowns).
export const sizeLabel = _sizeLabel;

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const _escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function useOrgAnonymizer({
  isSuperAdmin,
  viewAsOrg,
  scopedOrgId,
  orgs = [],
  orgIndustryMap = {},
  orgRegionMap = {},
  orgSizeMap = {},
  industries = [],
  filters = { industry: 'all', region: 'all', size: 'all' },
}) {
  const shouldAnonymize = !isSuperAdmin || !!viewAsOrg;
  const industryFilter = filters.industry || 'all';
  const regionFilter = filters.region || 'all';
  const sizeFilter = filters.size || 'all';

  return useMemo(() => {
    const industryNameById = {};
    industries.forEach((i) => {
      industryNameById[i.id] = i.name;
    });

    // De-dupe the incoming orgs list so multi-source callers (overview +
    // LEI + KPI, etc) don't end up with repeated ids.
    const nameById = {};
    const allIds = [];
    orgs.forEach((o) => {
      const id = o?.organization_id;
      if (id && !(id in nameById)) {
        nameById[id] = o.organization_name || id;
        allIds.push(id);
      }
    });

    // Deterministic peer labeling — alphabetical so the same org gets
    // the same letter across page re-renders and across surfaces.
    const orgLabelById = {};
    let peerIdx = 0;
    [...allIds]
      .sort((a, b) => (nameById[a] || '').localeCompare(nameById[b] || ''))
      .forEach((oid) => {
        const isYou = oid === scopedOrgId;
        const realName = nameById[oid] || oid;
        if (!shouldAnonymize) {
          orgLabelById[oid] = isYou ? `${realName} (You)` : realName;
        } else {
          orgLabelById[oid] = isYou
            ? `${realName} (You)`
            : `Org ${LETTERS[peerIdx++ % LETTERS.length]}`;
        }
      });

    const industryForOrg = (oid) =>
      industryNameById[orgIndustryMap[oid]] || null;
    const regionForOrg = (oid) => orgRegionMap[oid] || null;
    const sizeForOrg = (oid) => _sizeLabel(orgSizeMap[oid]);

    const matchesFilters = (oid) => {
      if (industryFilter !== 'all' && orgIndustryMap[oid] !== industryFilter) return false;
      if (regionFilter !== 'all' && (orgRegionMap[oid] || '__missing__') !== regionFilter) return false;
      if (sizeFilter !== 'all' && (orgSizeMap[oid] || '__missing__') !== sizeFilter) return false;
      return true;
    };

    const availableRegions = Array.from(
      new Set(Object.values(orgRegionMap).filter(Boolean)),
    ).sort();
    const availableSizes = Array.from(
      new Set(Object.values(orgSizeMap).filter(Boolean)),
    ).sort(
      (a, b) =>
        (SIZE_ORDER.indexOf(a) === -1 ? 99 : SIZE_ORDER.indexOf(a)) -
        (SIZE_ORDER.indexOf(b) === -1 ? 99 : SIZE_ORDER.indexOf(b)),
    );

    const renderOrgLabel = (orgId, fallbackName) => {
      const isYou = orgId === scopedOrgId;
      const name = orgLabelById[orgId] || fallbackName || orgId;
      const ind = industryForOrg(orgId);
      const reg = regionForOrg(orgId);
      const sz = sizeForOrg(orgId);
      return (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          <span
            className={
              isYou
                ? 'font-bold text-blue-700'
                : 'font-medium text-slate-800'
            }
          >
            {name}
          </span>
          {ind && (
            <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
              {ind}
            </span>
          )}
          {reg && (
            <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
              {reg}
            </span>
          )}
          {sz && (
            <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
              {sz}
            </span>
          )}
        </span>
      );
    };

    const OrgLabel = ({ orgId, fallback }) => renderOrgLabel(orgId, fallback);

    // Narrative / free-text anonymizer — swaps full org names only so we
    // don't accidentally clobber substrings inside unrelated prose.
    const anonymizeNarrative = (text) => {
      if (!text || !shouldAnonymize) return text || '';
      const replacements = allIds
        .map((oid) => {
          const real = nameById[oid];
          const label = orgLabelById[oid];
          if (!real || !label || real === label) return null;
          return { real, label };
        })
        .filter(Boolean)
        .sort((a, b) => b.real.length - a.real.length);
      let out = text;
      replacements.forEach(({ real, label }) => {
        out = out.replace(new RegExp(`\\b${_escapeRe(real)}\\b`, 'gi'), label);
      });
      return out;
    };

    // Cohort-name anonymizer — also strips the first brand token (≥3
    // chars) of each peer org so single-word references ("PPS 2026 New
    // Hires") get redacted. Scoped tenant is skipped; their own cohorts
    // stay readable.
    const anonymizeCohortName = (name) => {
      if (!name || !shouldAnonymize) return name || '';
      const replacements = [];
      allIds.forEach((oid) => {
        if (oid === scopedOrgId) return;
        const real = nameById[oid];
        const label = orgLabelById[oid];
        if (!real || !label) return;
        replacements.push({ pattern: real, label });
        const firstWord = real.split(/\s+/)[0];
        if (
          firstWord &&
          firstWord.length >= 3 &&
          firstWord.toLowerCase() !== real.toLowerCase()
        ) {
          replacements.push({ pattern: firstWord, label });
        }
      });
      replacements.sort((a, b) => b.pattern.length - a.pattern.length);
      let out = name;
      replacements.forEach(({ pattern, label }) => {
        out = out.replace(
          new RegExp(`\\b${_escapeRe(pattern)}\\b`, 'gi'),
          label,
        );
      });
      return out;
    };

    return {
      shouldAnonymize,
      orgLabelById,
      industryForOrg,
      regionForOrg,
      sizeForOrg,
      matchesFilters,
      availableRegions,
      availableSizes,
      renderOrgLabel,
      OrgLabel,
      anonymizeNarrative,
      anonymizeCohortName,
    };
  }, [
    shouldAnonymize,
    scopedOrgId,
    orgs,
    orgIndustryMap,
    orgRegionMap,
    orgSizeMap,
    industries,
    industryFilter,
    regionFilter,
    sizeFilter,
  ]);
}

export default useOrgAnonymizer;
